import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, CreditCard, Clock, RefreshCw, HelpCircle, Wallet } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import PublicHeader from "@/components/PublicHeader";

export default function RefundPolicy() {
  const navigate = useNavigate();
  const { settings } = usePlatform();
  const appTitle = settings?.title || "GEMS";

  return (
    <div className="min-h-screen bg-[#fdfdfd] text-slate-900 flex flex-col justify-between">
      <SEO
        title={`Refund Policy — ${appTitle}`}
        description={`Learn about ${appTitle} subscription refund eligibility, 7-day money-back guarantee, processing timelines, and support desk.`}
        keywords="refund policy, money back guarantee, subscription refunds, billing terms, cancellation policy"
        canonicalUrl="https://splitly.app/refund-policy"
        schemaType="WebPage"
      />
      {/* Header */}
      <PublicHeader showBack={true} />

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-12 flex-1 w-full">
        {/* Banner */}
        <div className="mb-10 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-50 border border-amber-200/80 text-xs font-semibold text-amber-700 mb-4">
            <RotateCcw className="w-4 h-4 text-amber-600" />
            Billing & Refunds
          </div>
          <h1 className="heading text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Refund Policy
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Last updated: August 27, 2026 • Effective immediately
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200/70 card-soft space-y-8 text-slate-700 text-sm leading-relaxed">
          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 grid place-items-center">
                <CreditCard className="w-4 h-4" />
              </div>
              1. Free & Premium Subscriptions
            </div>
            <p>
              <strong>{appTitle}</strong> offers core expense tracking and settlement ledger functionality for free. If you subscribe to premium features or paid plans, payments are processed securely via our payment gateways.
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center">
                <RotateCcw className="w-4 h-4" />
              </div>
              2. Refund Eligibility & 7-Day Window
            </div>
            <p className="mb-3">We want you to be completely satisfied with {appTitle}:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li><strong>7-Day Money-Back Guarantee:</strong> You may request a 100% refund on subscription fees within 7 days of initial subscription purchase.</li>
              <li><strong>Service Disruptions:</strong> If service is unavailable for more than 48 consecutive hours due to technical error on our platform, prorated refunds may be issued.</li>
              <li><strong>Duplicate Charges:</strong> Any billing errors or accidental duplicate charges will be refunded in full upon verification.</li>
            </ul>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">
                <Clock className="w-4 h-4" />
              </div>
              3. Refund Processing Timelines
            </div>
            <p>
              Once approved, refund requests are processed within <strong>5–7 business days</strong>. The refund will be credited back to your original method of payment (bank account, credit card, or digital wallet).
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-700 grid place-items-center">
                <RefreshCw className="w-4 h-4" />
              </div>
              4. Cancellations
            </div>
            <p>
              You may cancel your premium subscription at any time through your Profile or Subscription settings. Upon cancellation, your subscription will remain active until the end of the current billing cycle, with no additional charges.
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center">
                <HelpCircle className="w-4 h-4" />
              </div>
              5. How to Request a Refund
            </div>
            <p>
              To initiate a refund request, please submit your account details and payment transaction reference to our customer service desk at{" "}
              <a
                href="https://saharey.com/contactus"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-900 font-semibold underline underline-offset-2 hover:text-amber-600"
              >
                Saharey Support Desk
              </a>.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
